import { HTMLAttributes, useCallback, useEffect, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import { ButtonTheme, ButtonV2 } from 'builder/components/Button'
import Icon from 'builder/components/Icon'
import { useResumeDistributionPromo } from 'builder/components/ResumeDistributionModal/ResumeDistributionModal'
import DocumentActionMenu from 'builder/components/DocumentActionMenu'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { DownloadDocumentHandler } from 'builder/modules/resumeEditor'
import { CurrentDocument } from '../types'
import { FooterContainer, DownloadButton, CustomizeButton } from './styles'

type Props = {
  currentDocument: CurrentDocument
  triggerDownload?: boolean
  showResumeDistributionModal?: () => void
  isDarkBackground?: boolean
  handleCustomizeClick?: () => void
  handleDownloadDocument: DownloadDocumentHandler
  isFooterShadowVisible?: boolean
} & HTMLAttributes<HTMLDivElement>

export const DownloadOptionsFooter = (props: Props) => {
  const {
    triggerDownload,
    showResumeDistributionModal,
    currentDocument,
    isDarkBackground,
    handleCustomizeClick,
    handleDownloadDocument,
    isFooterShadowVisible,
  } = props
  const { i18n } = useI18n()
  const [downloadWasTriggered, setDownloadWasTriggered] = useState(false)
  const { shouldShowRDPromo } = useResumeDistributionPromo()
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const { isPhone } = useMediaQueries()
  const buttonSize = isPhone ? 'small' : 'default'

  const handleActionMenuOpen = useCallback(() => {
    setIsActionMenuOpen(true)
  }, [])

  useEffect(() => {
    if (triggerDownload && !downloadWasTriggered) {
      setDownloadWasTriggered(true)
      handleDownloadDocument()
    }
  }, [triggerDownload, handleDownloadDocument, downloadWasTriggered])

  const handleClick = useCallback(() => {
    if (shouldShowRDPromo() && showResumeDistributionModal) {
      showResumeDistributionModal?.()
      return
    }

    handleDownloadDocument()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDownloadDocument, showResumeDistributionModal])

  const handleShareClick = useCallback(() => {
    trackInternalEvent('click_share_button', { source: `${currentDocument?.type}_editor` })
  }, [currentDocument?.type])

  const handleActionMenuClose = useCallback(() => {
    setIsActionMenuOpen(false)
  }, [])

  return (
    <FooterContainer
      {...props}
      $isDarkBackground={isDarkBackground}
      $isFooterShadowVisible={isFooterShadowVisible}
    >
      <DownloadButton onClick={handleClick} size={buttonSize}>
        {i18n.t('builder.resume_editor.download')}
      </DownloadButton>
      <ButtonV2 onClick={handleActionMenuOpen} size={buttonSize}>
        <Icon.More />
      </ButtonV2>
      {handleCustomizeClick && (
        <CustomizeButton
          onClick={handleCustomizeClick}
          size={buttonSize}
          leadingIcon={<Icon.Grid />}
          theme={ButtonTheme.secondary}
        >
          {i18n.t('builder.resume_editor.customization')}
        </CustomizeButton>
      )}
      {isActionMenuOpen && (
        <DocumentActionMenu
          {...{
            position: 'top',
            documentId: currentDocument.id,
            onDownload: handleDownloadDocument,
            documentType: currentDocument.type,
            onClose: handleActionMenuClose,
            onShare: handleShareClick,
          }}
        />
      )}
    </FooterContainer>
  )
}
